import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, GraduationCap, Users, Globe, Award, BookOpen, Briefcase, Building, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import CallbackWidget from '../components/CallbackWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e44ed24-b725-410d-8d63-89f25832c44c/artifacts/nejwwhlw_Capture%20d%27%C3%A9cran%202026-02-11%20211549.png";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: "easeOut" }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const centers = [
  {
    id: 'diplomante',
    name: 'Centre de Formation Diplômante',
    path: '/diplomante',
    color: '#003366',
    lightColor: '#E6EEF5',
    icon: GraduationCap,
    description: 'Bachelor et Master en Business et Computer Science',
    image: 'https://images.unsplash.com/photo-1645263012668-a6617115f9b9?w=600&h=400&fit=crop'
  },
  {
    id: 'qualifiante',
    name: 'Centre de Formation Qualifiante',
    path: '/qualifiante',
    color: '#DC143C',
    lightColor: '#FCE8EC',
    icon: Briefcase,
    description: 'Formations professionnelles certifiantes',
    image: 'https://images.unsplash.com/photo-1758876202167-f81c995c3fdc?w=600&h=400&fit=crop'
  },
  {
    id: 'certifiante',
    name: 'Centre de Formation Certifiante',
    path: '/certifiante',
    color: '#FFD700',
    lightColor: '#FFF9E6',
    icon: Award,
    description: 'Certificats spécialisés pour professionnels',
    image: 'https://images.unsplash.com/photo-1758519290111-bfbd61b32d49?w=600&h=400&fit=crop'
  },
  {
    id: 'linguistique',
    name: 'Centre de Formation Linguistique',
    path: '/linguistique',
    color: '#8B5CF6',
    lightColor: '#F3EFFE',
    icon: Globe,
    description: 'Séjours linguistiques et anglais des affaires',
    image: 'https://images.unsplash.com/photo-1620829813573-7c9e1877706f?w=600&h=400&fit=crop'
  }
];

const stats = [
  { number: '4', label: 'Centres de formation' },
  { number: '50+', label: 'Filières disponibles' },
  { number: '1', label: 'Campus' },
  { number: '100%', label: 'Pratique' }
];

const distinctions = [
  'Partenariat BIT (Bureau International du Travail)',
  'Agréé FDFP (Fonds de Développement de la Formation Professionnelle)',
  'Membre du réseau des Business Schools africaines',
  'Certifié ISO 9001 - Qualité de formation'
];

export default function Home() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/content`)
      .then(r => r.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  const heroTitle = content?.hero?.title || 'Construisez votre avenir avec CEFAT Business School';
  const heroSubtitle = content?.hero?.subtitle || 'De la formation professionnelle au MBA, decouvrez des parcours d\'excellence adaptes a vos ambitions. 4 centres specialises, une seule mission : votre reussite.';
  const heroCta = content?.hero?.cta_text || 'Decouvrir nos formations';
  const heroCtaLink = content?.hero?.cta_link || '/preinscription';
  const dynamicStats = content?.stats || stats;
  const dynamicTestimonials = content?.testimonials || null;

  return (
    <div className="overflow-hidden">
      <SEO
        title="Ecole de Commerce & Business School a Abidjan"
        description="CEFAT Business School Abidjan : Bachelor, MBA, formations diplomantes, qualifiantes, certifiantes et linguistiques. 4 centres d'excellence en Cote d'Ivoire. Preinscription en ligne."
        keywords="ecole de commerce Abidjan, business school Cote d'Ivoire, MBA Abidjan, Bachelor Abidjan, formation professionnelle Abidjan, CEFAT, formation diplomante Abidjan, formation qualifiante, formation certifiante, sejour linguistique Cote d'Ivoire, ecole de management Afrique"
        url="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient grain-overlay" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-[#DC143C]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] bg-[#FFD700]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-[#FFD700] text-[#003366] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Excellence Academique en Afrique
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" data-testid="hero-title">
                {heroTitle}
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed" data-testid="hero-subtitle">
                {heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to={heroCtaLink}>
                  <Button 
                    className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    data-testid="hero-preinscription-btn"
                  >
                    Je me préinscris <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/a-propos">
                  <Button 
                    variant="outline"
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#003366] rounded-full px-8 py-6 text-lg font-bold"
                    data-testid="hero-about-btn"
                  >
                    Découvrir CEFAT
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1694175271713-a6e2cc378980?w=600&h=700&fit=crop"
                  alt="Étudiant CEFAT confiant"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#DC143C] rounded-full flex items-center justify-center">
                      <Award className="text-white" size={28} />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-[#003366] text-lg">+20 ans</p>
                      <p className="text-gray-500 text-sm">d'excellence</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {dynamicStats.map((stat, index) => (
              <motion.div key={index} variants={staggerItem} className="stat-item">
                <p className="stat-number">{stat.number}</p>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Centers Section */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="centers-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-blue mb-4">Nos Formations</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#003366] mb-4">
              4 Centres d'Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Chaque centre répond à des besoins spécifiques de formation, du diplôme académique à la certification professionnelle.
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {centers.map((center) => (
              <motion.div key={center.id} variants={staggerItem}>
                <Link to={center.path} data-testid={`center-card-${center.id}`}>
                  <div className="center-card h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={center.image} 
                        alt={center.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div 
                        className="absolute top-4 left-4 w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: center.color }}
                      >
                        <center.icon className="text-white" size={24} />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-[#003366] mb-2">
                        {center.name}
                      </h3>
                      <p className="text-gray-600 mb-4">{center.description}</p>
                      <span 
                        className="inline-flex items-center gap-2 font-semibold transition-colors"
                        style={{ color: center.color }}
                      >
                        Découvrir <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="badge badge-red mb-4">Pourquoi nous choisir</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#003366] mb-6">
                L'excellence académique au service de votre carrière
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                CEFAT Business School combine rigueur académique internationale et ancrage africain 
                pour former les leaders de demain. Nos programmes sont conçus en partenariat avec 
                les entreprises pour garantir votre employabilité.
              </p>

              <div className="space-y-4 mb-8">
                {distinctions.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#16a34a] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/a-propos">
                <Button className="bg-[#003366] hover:bg-[#002244] text-white rounded-full px-8 py-3 font-semibold">
                  En savoir plus <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1638636214032-581196ffd400?w=300&h=400&fit=crop"
                    alt="Campus CEFAT"
                    className="rounded-xl shadow-lg"
                  />
                  <div className="bg-[#003366] text-white p-6 rounded-xl">
                    <p className="font-heading text-3xl font-bold mb-1">4</p>
                    <p className="text-white/70">Campus en Afrique</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-[#DC143C] text-white p-6 rounded-xl">
                    <p className="font-heading text-3xl font-bold mb-1">50+</p>
                    <p className="text-white/70">Programmes</p>
                  </div>
                  <img 
                    src="https://images.unsplash.com/photo-1694175271713-a6e2cc378980?w=300&h=350&fit=crop"
                    alt="Étudiants CEFAT"
                    className="rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials placeholder */}
      <section className="py-20 md:py-32 bg-[#003366] grain-overlay" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge bg-[#FFD700] text-[#003366] mb-4">Témoignages</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              Ce que disent nos diplômés
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {(dynamicTestimonials || [
              {
                name: 'Aminata K.',
                role: 'MBA Finance 2023',
                text: 'Grace a CEFAT, j\'ai pu decrocher un poste de directrice financiere dans une multinationale. La qualite de l\'enseignement est exceptionnelle.'
              },
              {
                name: 'Koffi M.',
                role: 'BBA 2022',
                text: 'L\'accompagnement personnalise et les stages en entreprise m\'ont permis de creer ma propre startup des la fin de mes etudes.'
              },
              {
                name: 'Fatoumata D.',
                role: 'Community Manager 2023',
                text: 'Formation pratique et professeurs a l\'ecoute. Je recommande CEFAT a tous ceux qui veulent se former aux metiers du digital.'
              }
            ]).map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="testimonial-card"
              >
                <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#003366] rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#003366]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Callback Widget */}
      <section className="py-20 md:py-24 bg-white" data-testid="callback-section">
        <div className="max-w-5xl mx-auto px-4">
          <CallbackWidget variant="inline" />
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <span className="badge badge-blue mb-4">Blog & Actualités</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
                Dernières actualités
              </h2>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="mt-4 md:mt-0 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white rounded-full">
                Voir tout le blog <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Rentrée académique 2024-2025',
                excerpt: 'Découvrez les dates clés et les nouveautés de cette nouvelle année académique.',
                category: 'Actualités',
                date: '15 Nov 2024'
              },
              {
                title: 'Partenariat avec le BIT',
                excerpt: 'CEFAT renforce son partenariat avec le Bureau International du Travail.',
                category: 'Partenariats',
                date: '10 Nov 2024'
              },
              {
                title: 'Cérémonie de remise des diplômes',
                excerpt: 'Plus de 500 étudiants diplômés lors de la cérémonie annuelle.',
                category: 'Événements',
                date: '5 Nov 2024'
              }
            ].map((post, index) => (
              <motion.article 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-md card-hover"
              >
                <div className="h-48 bg-gradient-to-br from-[#003366] to-[#001a33]" />
                <div className="p-6">
                  <span className="badge badge-blue text-xs">{post.category}</span>
                  <h3 className="font-heading text-xl font-bold text-[#003366] mt-3 mb-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <p className="text-sm text-gray-400">{post.date}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
